import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { t } = useI18n();
  const { login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ mobile: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(form.mobile, form.password);
      const next = params.get("next");
      if (next) nav(next);
      else if (u.role === "customer") nav("/dashboard");
      else nav("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center py-10 bg-slate-50/40" data-testid="login-page">
      <div className="bg-white rounded-2xl border border-slate-200 nc-soft-shadow p-8 w-full max-w-md">
        <div className="w-12 h-12 rounded-xl nc-gradient mx-auto flex items-center justify-center text-white font-bold text-xl">N</div>
        <h1 className="mt-4 text-center text-2xl font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>{t("auth.loginTitle")}</h1>
        <p className="text-center text-sm text-slate-500 mt-1">{t("auth.loginSub")}</p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{t("auth.mobile")}</label>
            <input className="nc-input mt-1" data-testid="login-mobile-input" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} required maxLength="10" pattern="[0-9]{10}" />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{t("auth.password")}</label>
              <Link to="/forgot-password" className="text-xs font-medium text-emerald-700 hover:text-emerald-800" data-testid="goto-forgot-password-link">
                {t("auth.forgotPassword")}
              </Link>
            </div>
            <input type="password" className="nc-input mt-1" data-testid="login-password-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required />
          </div>
          {error && <p className="text-sm text-rose-600" data-testid="login-error">{error}</p>}
          <button type="submit" disabled={loading} className="nc-btn-primary w-full justify-center" data-testid="login-submit-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}{t("auth.loginCta")}
          </button>
        </form>
        <div className="mt-5 text-center text-sm text-slate-600">
          {t("auth.noAccount")} <Link to="/signup" className="text-emerald-700 font-medium" data-testid="goto-signup-link">{t("auth.signupCta")}</Link>
        </div>
        <div className="mt-2 text-center text-sm">
          <Link to="/partner/register" className="text-slate-600 hover:text-emerald-700" data-testid="goto-partner-register-link">{t("auth.partnerCta")}</Link>
        </div>
      </div>
    </div>
  );
}
